import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, Instagram, MessageCircle, ArrowRight, MapPin, Shield, Sparkles, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AISearchBar from '../components/AISearchBar';
import SEOHead from '../components/SEOHead';
import HimalayanFestivalBanner from '../components/HimalayanFestivalBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_ADJECTIVES = ['handpicked', 'unique', 'curated', 'boutique', 'hidden', 'distinctive', 'soulful'];

/**
 * CollectionsStrip — themed bundle banners (Family Estate, Couples-only, …).
 * Sits below the Valley strip. Each card opens /collection/<slug>. If no
 * collections are configured, renders nothing (zero homepage impact).
 */
const CollectionCard = ({ c }) => {
  // Rotate through the collection's stay images every 3.5s with a soft fade.
  const preview = (c.preview_images && c.preview_images.length ? c.preview_images : [c.hero_image]).filter(Boolean);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (preview.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % preview.length), 3500);
    return () => clearInterval(t);
  }, [preview.length]);
  return (
    <Link to={`/collection/${c.slug}`}
          className="group relative block rounded-2xl overflow-hidden aspect-[16/10] shadow-md hover:shadow-2xl transition-shadow"
          data-testid={`home-collection-${c.slug}`}>
      {preview.length ? (
        preview.map((src, i) => (
          <img key={src + i} src={src} alt={c.name}
               className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-1000 ease-in-out"
               style={{ opacity: i === idx ? 1 : 0, transitionProperty: 'opacity, transform' }} />
        ))
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-stone-700 to-stone-500" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      {c.badge && (
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-stone-700 text-[10px] font-semibold tracking-[0.18em] uppercase px-2.5 py-1 rounded">
          {c.badge}
        </div>
      )}
      {preview.length > 1 && (
        <div className="absolute top-4 right-4 flex gap-1" data-testid={`home-collection-dots-${c.slug}`}>
          {preview.map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all ${i === idx ? 'w-4 bg-white' : 'w-1 bg-white/40'}`} />
          ))}
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h3 className="text-2xl font-bold text-white mb-1"
            style={{ fontFamily: 'Playfair Display, serif' }}>{c.name}</h3>
        {c.tagline && (
          <p className="text-white/80 text-sm italic mb-2"
             style={{ fontFamily: 'Playfair Display, serif' }}>{c.tagline}</p>
        )}
        <div className="flex items-center gap-1 text-white/70 text-xs group-hover:text-white transition-colors">
          Explore <ArrowRight className="h-3 w-3" />
        </div>
      </div>
    </Link>
  );
};

const CollectionsStrip = () => {
  const [collections, setCollections] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/api/curated/collections`)
      .then(r => r.json())
      .then(j => setCollections(j.collections || []))
      .catch(() => {});
  }, []);
  if (!collections.length) return null;
  return (
    <section className="py-12 px-6 lg:px-8" data-testid="collections-strip">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-px bg-[#3a5a40]" />
          <p className="text-xs tracking-[0.3em] text-[#3a5a40] uppercase">Curated Collections</p>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2"
            style={{ fontFamily: 'Playfair Display, serif' }}>
          Themed bundles for the way you travel
        </h2>
        <p className="text-stone-500 text-sm mb-8 max-w-xl">
          Hand-picked groupings of cottages — by who you&rsquo;re travelling with, not where.
        </p>
        <div className={`grid gap-5 ${collections.length === 1 ? 'sm:grid-cols-1' : 'sm:grid-cols-2'} lg:grid-cols-${Math.min(collections.length, 3)}`}>
          {collections.map((c) => (<CollectionCard key={c.slug} c={c} />))}
        </div>
      </div>
    </section>
  );
};

const ValleyMapStrip = () => {
  const navigate = useNavigate();

  return (
    <div className="py-6 px-6 lg:px-8" data-testid="valley-strip-light">
      <div className="max-w-6xl mx-auto">
        <div
          className="relative rounded-2xl overflow-hidden cursor-pointer group"
          style={{height: '240px'}}
          onClick={() => navigate('/valley/tirthan')}
        >
          {/* Background: Bright Tirthan Valley with sky and river */}
          <img
            src="/tirthan_valley_bright.jpg"
            alt="Tirthan Valley"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20" />

          {/* Content */}
          <div className="absolute inset-0 flex items-center px-10 lg:px-16">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2.5 h-2.5 rounded-full bg-[#5a9e6e] animate-pulse" />
                <span className="text-xs tracking-[0.25em] text-white/70 uppercase font-medium">GoHimalayan Valley</span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-bold text-white mb-2" style={{fontFamily: 'Playfair Display, serif'}}>
                Tirthan Valley
              </h3>
              <p className="text-white/65 text-base lg:text-lg" style={{fontFamily: 'Playfair Display, serif', fontStyle: 'italic'}}>
                Where the Great Himalayan National Park begins
              </p>
            </div>

            {/* Stats */}
            <div className="hidden md:flex items-center gap-8 lg:gap-12">
              <div className="text-center">
                <p className="text-3xl font-bold text-white">6</p>
                <p className="text-xs text-white/55 uppercase tracking-wider">Stays</p>
              </div>
              <div className="w-px h-12 bg-white/15" />
              <div className="text-center">
                <p className="text-3xl font-bold text-white">4</p>
                <p className="text-xs text-white/55 uppercase tracking-wider">Partners</p>
              </div>
              <div className="w-px h-12 bg-white/15" />
              <div className="text-center">
                <p className="text-base font-medium text-white/85" style={{fontFamily: 'Playfair Display, serif'}}>Raju Bharti's Cottage</p>
                <p className="text-xs text-[#7ab88a] uppercase tracking-wider">Valley Partner</p>
              </div>
              <div className="w-px h-12 bg-white/15" />
              <div className="flex items-center gap-2 text-white/75 group-hover:text-white group-hover:gap-3 transition-all text-base font-medium">
                Explore <ArrowRight className="h-5 w-5" />
              </div>
            </div>

            {/* Mobile CTA */}
            <div className="md:hidden flex items-center gap-2 text-white/80 text-base font-medium">
              Explore <ArrowRight className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Landing = () => {
  const [featuredStays, setFeaturedStays] = useState([]);
  const [adjectiveIndex, setAdjectiveIndex] = useState(0);
  const [adjectiveFade, setAdjectiveFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setAdjectiveFade(false);
      setTimeout(() => {
        setAdjectiveIndex(prev => (prev + 1) % HERO_ADJECTIVES.length);
        setAdjectiveFade(true);
      }, 500);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/curated/stays?limit=10`)
      .then(r => r.json())
      .then(d => setFeaturedStays(d.stays || []))
      .catch(() => {});
  }, []);

  const openWhatsApp = () => {
    window.open('https://wa.me/919871155963?text=Hi!%20I%27m%20interested%20in%20GoHimalayan.', '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEOHead
        title="Curated Himalayan Stays"
        description="Discover handpicked, authentic stays across the Indian Himalayas. From forest retreats in Binsar to riverside lodges in Tirthan Valley — curated for slow travellers."
        url="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "GoHimalayan",
          "url": "https://gohimalayan.com",
          "description": "Curated stays across the Indian Himalayas",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://gohimalayan.com/discovered?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <Mountain className="h-7 w-7 text-[#2C5F8D]" strokeWidth={2.5} />
              <span className="text-lg font-bold tracking-tight text-gray-900" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
            <div className="flex items-center gap-5">
              <Link 
                to="/about"
                className="hidden sm:flex text-gray-500 hover:text-gray-900 transition-colors text-sm"
                data-testid="about-link"
              >
                About
              </Link>
              <a 
                href="https://instagram.com/gohimalayan" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden sm:flex items-center text-gray-500 hover:text-[#E1306C] transition-colors"
                data-testid="instagram-link"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <button 
                onClick={openWhatsApp}
                className="hidden sm:flex items-center text-gray-500 hover:text-[#25D366] transition-colors"
                data-testid="whatsapp-header-btn"
              >
                <MessageCircle className="h-4 w-4" />
              </button>
              <Link 
                to="/host"
                className="px-3 sm:px-5 py-1.5 sm:py-2 bg-gray-900 text-white text-[11px] sm:text-sm font-medium rounded-full hover:bg-gray-800 transition-all whitespace-nowrap"
                data-testid="partner-cta"
              >
                <span className="sm:hidden">Host</span>
                <span className="hidden sm:inline">Host with us</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="max-w-xl mx-auto mb-8">
            <AISearchBar />
          </div>
          <p className="text-xs font-medium tracking-[0.3em] text-gray-400 uppercase mb-5">
            INDIA · NEPAL · BHUTAN · TIBET
          </p>
          <h1 className="text-2xl sm:text-3xl lg:text-[2.5rem] font-bold text-gray-900 mb-5 leading-[1.2] tracking-tight" style={{fontFamily: 'Playfair Display, serif'}}>
            Discover{' '}
            <span
              className="text-[#2C5F8D] inline-block italic"
              style={{ transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)', opacity: adjectiveFade ? 1 : 0, transform: adjectiveFade ? 'translateY(0)' : 'translateY(4px)' }}
              data-testid="landing-hero-adjective"
            >
              {HERO_ADJECTIVES[adjectiveIndex]} stays
            </span>{' '}
            across the Himalayas.
          </h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed mb-8">
            A bespoke collection of distinctive homes, curated for character, authenticity, and connection to place.
          </p>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#2C5F8D]" />
              <span>Every stay personally vetted</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#2C5F8D]" />
              <span>A growing collection</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#2C5F8D]" />
              <span>Across Himalayan valleys</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Stays — Bento Grid */}
      <section className="pb-8 px-6 lg:px-8" data-testid="featured-stays-section">
        <div className="max-w-7xl mx-auto">
          {featuredStays.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[260px]">
              {/* Large Featured Card — spans 7 cols, 2 rows */}
              <Link
                to={`/discovered?highlight=${encodeURIComponent(featuredStays[0]?.display_name || '')}`}
                className="md:col-span-7 md:row-span-2 group relative rounded-2xl overflow-hidden cursor-pointer"
                data-testid="featured-stay-hero"
              >
                <img
                  src={featuredStays[0]?.images?.[0]?.url}
                  alt={featuredStays[0]?.display_name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 img-enhance"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-white/60 text-xs font-medium tracking-widest uppercase mb-2">Discovered Stay</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{fontFamily: 'Playfair Display, serif'}}>
                    {featuredStays[0]?.display_name}
                  </h3>
                  <p className="text-white/70 text-sm flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {featuredStays[0]?.valley}
                  </p>
                </div>
              </Link>

              {/* Two Stacked Right Cards */}
              {featuredStays.slice(1, 3).map((stay, i) => (
                <Link
                  key={stay.gohimalayan_id || i}
                  to={`/discovered?highlight=${encodeURIComponent(stay.display_name || '')}`}
                  className="md:col-span-5 group relative rounded-2xl overflow-hidden cursor-pointer"
                  data-testid={`featured-stay-${i + 1}`}
                >
                  <img
                    src={stay.images?.[0]?.url}
                    alt={stay.display_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 img-enhance"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h4 className="text-lg font-semibold text-white mb-1" style={{fontFamily: 'Playfair Display, serif'}}>
                      {stay.display_name}
                    </h4>
                    <p className="text-white/60 text-xs flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {stay.valley}
                    </p>
                  </div>
                </Link>
              ))}

              {/* Mid Row — Three Cards */}
              {featuredStays.slice(3, 6).map((stay, i) => (
                <Link
                  key={stay.gohimalayan_id || i}
                  to={`/discovered?highlight=${encodeURIComponent(stay.display_name || '')}`}
                  className="md:col-span-4 group relative rounded-2xl overflow-hidden cursor-pointer"
                  data-testid={`featured-stay-${i + 3}`}
                >
                  <img
                    src={stay.images?.[0]?.url}
                    alt={stay.display_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 img-enhance"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h4 className="text-base font-semibold text-white mb-1" style={{fontFamily: 'Playfair Display, serif'}}>
                      {stay.display_name}
                    </h4>
                    <p className="text-white/60 text-xs flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {stay.valley}
                    </p>
                  </div>
                </Link>
              ))}

              {/* Bottom Row — Four Cards */}
              {featuredStays.slice(6, 10).map((stay, i) => (
                <Link
                  key={stay.gohimalayan_id || i}
                  to={`/discovered?highlight=${encodeURIComponent(stay.display_name || '')}`}
                  className="md:col-span-3 group relative rounded-2xl overflow-hidden cursor-pointer"
                  data-testid={`featured-stay-${i + 6}`}
                >
                  <img
                    src={stay.images?.[0]?.url}
                    alt={stay.display_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 img-enhance"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h4 className="text-sm font-semibold text-white mb-0.5" style={{fontFamily: 'Playfair Display, serif'}}>
                      {stay.display_name}
                    </h4>
                    <p className="text-white/50 text-[11px] flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {stay.valley}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Valley Strip — Tirthan Valley */}
      <ValleyMapStrip />

      {/* Themed Collections Strip — Family, Couples, Pet-friendly, etc. */}
      <CollectionsStrip />

      {/* View All Stays CTA */}
      <section className="pb-16 pt-4 px-6 lg:px-8 text-center">
        <Link
          to="/discovered"
          className="inline-flex items-center gap-3 px-10 py-4 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-all hover:-translate-y-0.5 shadow-lg"
          data-testid="view-all-stays-cta"
        >
          View All Stays
          <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* Dual CTA Section - Travellers & Hosts */}
      <section className="py-20 px-6 lg:px-8 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 mb-12">
            {/* For Discerning Travellers */}
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-sm font-medium tracking-widest uppercase mb-4">
                For Discerning Travellers
              </p>
              <h2 
                className="text-2xl sm:text-3xl font-bold text-white mb-4"
                style={{fontFamily: 'Playfair Display, serif'}}
              >
                Experience the Himalayas. Thoughtfully.
              </h2>
              <p className="text-gray-400">
                Curated homes. Immersive journeys. A deeper connection to place.
              </p>
            </div>

            {/* For Independent Hosts */}
            <div className="text-center md:text-left md:border-l md:border-gray-700 md:pl-12 lg:pl-16">
              <p className="text-gray-400 text-sm font-medium tracking-widest uppercase mb-4">
                For Independent Hosts
              </p>
              <h2 
                className="text-2xl sm:text-3xl font-bold text-white mb-4"
                style={{fontFamily: 'Playfair Display, serif'}}
              >
                Join a Curated Himalayan Platform.
              </h2>
              <p className="text-gray-400">
                Selective partnership. Refined positioning. A discerning audience.
              </p>
            </div>
          </div>

          {/* Centered CTA */}
          <div className="text-center">
            <button 
              onClick={openWhatsApp}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-gray-900 rounded-full font-semibold hover:bg-gray-100 transition-all"
              data-testid="connect-cta"
            >
              Connect with us
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] hover:bg-[#1da851] rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-110 z-50"
        data-testid="whatsapp-float-btn"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </button>

      {/* Himalayan Festival Calendar */}
      <HimalayanFestivalBanner />

      {/* Minimal Footer */}
      <footer className="py-8 px-6 lg:px-8 border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Mountain className="h-5 w-5 text-[#2C5F8D]" />
            <span className="text-sm text-gray-500">© 2026 GoHimalayan</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/about" className="text-gray-400 hover:text-stone-600 transition-colors text-[10px] tracking-wide uppercase" data-testid="footer-about-link">About</Link>
            <Link to="/host" className="text-gray-400 hover:text-stone-600 transition-colors text-[10px] tracking-wide uppercase" data-testid="footer-host-link">Host with us</Link>
            <a
              href="https://wa.me/919871155963?text=Hi%20—%20we're%20a%20community%2Fretreat%20group%20looking%20to%20partner%20with%20GoHimalayan."
              target="_blank" rel="noopener noreferrer"
              className="text-gray-400 hover:text-stone-600 transition-colors text-[10px] tracking-wide uppercase hidden sm:inline"
              data-testid="footer-communities-link"
            >
              For Communities & Retreats
            </a>
            <Link to="/invest" className="text-gray-400 hover:text-stone-600 transition-colors text-[10px] tracking-wide uppercase hidden sm:inline" data-testid="footer-invest-link">Investors</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-stone-600 transition-colors text-[10px] tracking-wide uppercase" data-testid="privacy-policy-link">Privacy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-stone-600 transition-colors text-[10px] tracking-wide uppercase" data-testid="terms-link">Terms</Link>
            <Link 
              to="/states/himachal"
              className="text-gray-400 hover:text-[#2C5F8D] transition-colors text-[10px] tracking-wide uppercase hidden md:inline"
              data-testid="state-partnership-link"
            >
              State Tourism
            </Link>
            <a 
              href="https://instagram.com/gohimalayan" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#E1306C] transition-colors"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <button 
              onClick={openWhatsApp}
              className="text-gray-400 hover:text-[#25D366] transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
